// app/actions/discussion-actions.ts
"use server";

import { prisma } from "@/lib/prisma";
import { revalidatePath } from "next/cache";
import { auth } from "@/lib/auth";
import { headers } from "next/headers";

// Create a new discussion (post)
export async function createDiscussionAction(formData: {
  body: string;
  tags: string[];
  category?: string;
}) {
  try {
    const session = await auth.api.getSession({
      headers: await headers(),
    });
    if (!session) {
      throw new Error("Unauthorized");
    }

    const userId = BigInt(session?.user.id!);

    const tagData = formData.tags?.map((tag) => ({
      created_at: new Date(),
      updated_at: new Date(),
      tags: tag.toString(),
      status: 1,
    })) || [];

    const discussion = await prisma.discussions.create({
      data: {
        body: formData.body,
        created_at: new Date(),
        updated_at: new Date(),
        // Don't create a like automatically - let user like it themselves
        tags: {
          create: tagData,
        },
      },
      include: {
        likes: {
          include: {
            users: true,
          },
        },
        replies: true,
        tags: true,
      },
    });

    revalidatePath("/feed");
    return { success: true, discussion };
  } catch (error) {
    console.error("Error creating discussion:", error);
    return { success: false, error: "Failed to create discussion" };
  }
}

// Get all discussions with likes and comments count
export async function getDiscussionsAction(options?: {
  page?: number;
  limit?: number;
  userId?: bigint;
}) {
  try {
    const page = options?.page || 1;
    const limit = options?.limit || 20;
    const skip = (page - 1) * limit;

    const discussions = await prisma.discussions.findMany({
      skip,
      take: limit,
      orderBy: {
        created_at: "desc",
      },
      where: {
        deleted_at: null,
      },
      include: {
        likes: {
          include: {
            users: {
              include: {
                roles: true,
              },
            },
          },
        },
        replies: {
          include: {
            users_replies_created_byTousers: true,
            users_replies_reply_toTousers: true,
          },
        },
        tags: true,
      },
    });

    // Transform to include counts and liked users
    const discussionsWithCounts = discussions.map(discussion => {
      const likesCount = discussion.likes.length;
      const commentsCount = discussion.replies.length;
      
      // Get users who liked this discussion
      const likedUsers = discussion.likes.map(like => ({
        id: like.users.id.toString(),
        name: like.users.name,
        email: like.users.email,
        avatar: like.users.avatar,
        role: like.users.roles?.name || 'User'
      }));

      // Check if specific user liked this discussion
      const isLikedByUser = options?.userId 
        ? discussion.likes.some(like => like.liked_by === options.userId)
        : false;

      return {
        ...discussion,
        likesCount,
        commentsCount,
        likedUsers,
        isLikedByUser
      };
    });

    const total = await prisma.discussions.count({
      where: {
        deleted_at: null,
      },
    });

    return {
      success: true,
      discussions: discussionsWithCounts,
      pagination: {
        page,
        limit,
        total,
        pages: Math.ceil(total / limit),
      },
    };
  } catch (error) {
    console.error("Error fetching discussions:", error);
    return { success: false, error: "Failed to fetch discussions" };
  }
}

// Like/Unlike a discussion
export async function toggleLikeDiscussionAction(discussionId: number, userId: bigint) {
  try {
    // Check if already liked
    const existingLike = await prisma.likes.findFirst({
      where: {
        discussions_id: discussionId,
        liked_by: userId,
      },
    });

    if (existingLike) {
      // Unlike
      await prisma.likes.delete({
        where: {
          id: existingLike.id,
        },
      });
      console.log(`User ${userId} unliked discussion ${discussionId}`);
    } else {
      // Like
      await prisma.likes.create({
        data: {
          discussions_id: discussionId,
          liked_by: userId,
          liked_who: userId,
          created_at: new Date(),
          updated_at: new Date(),
          status: 1,
        },
      });
      console.log(`User ${userId} liked discussion ${discussionId}`);
    }

    revalidatePath("/feed");
    revalidatePath(`/discussion/${discussionId}`);
    return { success: true };
  } catch (error) {
    console.error("Error toggling like on discussion:", error);
    return { success: false, error: "Failed to toggle like" };
  }
}

// Create a reply
export async function createReplyAction(formData: {
  discussionId: number;
  replyText: string;
  replyTo?: bigint;
}) {
  try {
    const session = await auth.api.getSession({
      headers: await headers(),
    });
    if (!session) {
      throw new Error("Unauthorized");
    }
    const userId = BigInt(session?.user?.id!);

    const reply = await prisma.replies.create({
      data: {
        discussions_id: formData.discussionId,
        created_by: userId,
        reply_to: formData.replyTo || userId,
        content: formData.replyText,
        created_at: new Date(),
        updated_at: new Date(),
        status: 1,
      },
      include: {
        users_replies_created_byTousers: {
          include: {
            roles: true,
          },
        },
        users_replies_reply_toTousers: true,
      },
    });

    revalidatePath("/feed");
    revalidatePath(`/discussion/${formData.discussionId}`);
    return { success: true, reply };
  } catch (error) {
    console.error("Error creating reply:", error);
    return { success: false, error: "Failed to create reply" };
  }
}

// Get specific discussion with detailed info
export async function getDiscussionByIdAction(discussionId: number, userId?: bigint) {
  try {
    const discussion = await prisma.discussions.findUnique({
      where: {
        id: discussionId,
        deleted_at: null,
      },
      include: {
        likes: {
          include: {
            users: {
              include: {
                roles: true,
              },
            },
          },
        },
        replies: {
          include: {
            users_replies_created_byTousers: {
              include: {
                roles: true,
              },
            },
            users_replies_reply_toTousers: true,
          },
          orderBy: {
            created_at: "desc",
          },
        },
        tags: true,
      },
    });

    if (!discussion) {
      return { success: false, error: "Discussion not found" };
    }

    const likesCount = discussion.likes.length;
    const commentsCount = discussion.replies.length;
    const likedUsers = discussion.likes.map(like => ({
      id: like.users.id.toString(),
      name: like.users.name,
      email: like.users.email,
      avatar: like.users.avatar,
      role: like.users.roles?.name || 'User'
    }));
    const isLikedByUser = userId 
      ? discussion.likes.some(like => like.liked_by === userId)
      : false;

    const discussionWithCounts = {
      ...discussion,
      likesCount,
      commentsCount,
      likedUsers,
      isLikedByUser
    };

    return { success: true, discussion: discussionWithCounts };
  } catch (error) {
    console.error("Error fetching discussion:", error);
    return { success: false, error: "Failed to fetch discussion" };
  }
}

// Get discussions from users you follow
export async function getFollowingDiscussionsAction(userId: bigint) {
  try {
    // Get users that this user follows
    const following = await prisma.follow.findMany({
      where: {
        user_id: userId,
        status: 1,
      },
      select: {
        follow_who: true,
      },
    });

    const followingIds = following.map((f) => BigInt(f.follow_who));

    // Get posts from followed users
    const discussions = await prisma.discussions.findMany({
      where: {
        deleted_at: null,
        likes: {
          some: {
            liked_by: {
              in: [...followingIds, userId],
            },
          },
        },
      },
      orderBy: {
        created_at: "desc",
      },
      include: {
        likes: {
          include: {
            users: true,
          },
        },
        replies: {
          include: {
            users_replies_created_byTousers: true,
            users_replies_reply_toTousers: true,
          },
        },
        tags: true,
      },
    });

    const discussionsWithCounts = discussions.map(discussion => {
      const likesCount = discussion.likes.length;
      const commentsCount = discussion.replies.length;
      const likedUsers = discussion.likes.map(like => ({
        id: like.users.id.toString(),
        name: like.users.name,
        email: like.users.email,
        avatar: like.users.avatar
      }));
      const isLikedByUser = discussion.likes.some(like => like.liked_by === userId);

      return {
        ...discussion,
        likesCount,
        commentsCount,
        likedUsers,
        isLikedByUser
      };
    });

    return { success: true, discussions: discussionsWithCounts };
  } catch (error) {
    console.error("Error fetching following discussions:", error);
    return { success: false, error: "Failed to fetch following discussions" };
  }
}

// Delete a discussion
export async function deleteDiscussionAction(discussionId: number, userId: bigint) {
  try {
    // Verify ownership - check if user is the author
    // Since we don't have direct author field, check if user created the first like (author)
    const discussion = await prisma.discussions.findFirst({
      where: {
        id: discussionId,
      },
      include: {
        likes: {
          where: {
            liked_by: userId,
          },
        },
      },
    });

    if (!discussion) {
      throw new Error("Discussion not found");
    }

    // User can delete if they're the author (created the discussion)
    // or you can add admin role check here
    await prisma.discussions.update({
      where: { id: discussionId },
      data: { deleted_at: new Date() },
    });

    revalidatePath("/feed");
    return { success: true };
  } catch (error) {
    console.error("Error deleting discussion:", error);
    return { success: false, error: "Failed to delete discussion" };
  }
}

// Get replies for a discussion
export async function getRepliesAction(discussionId: number) {
  try {
    const replies = await prisma.replies.findMany({
      where: {
        discussions_id: discussionId,
        status: 1,
      },
      orderBy: {
        created_at: "desc",
      },
      include: {
        users_replies_created_byTousers: {
          include: {
            roles: true,
          },
        },
        users_replies_reply_toTousers: {
          include: {
            roles: true,
          },
        },
      },
    });

    return { success: true, replies };
  } catch (error) {
    console.error("Error fetching replies:", error);
    return { success: false, error: "Failed to fetch replies" };
  }
}